import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Star, X, Loader2, CheckCircle } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import axios from 'axios';

const RatingModal = ({ isOpen, onClose, rideId, driverName, onRatingSubmitted }) => {
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const handleSubmit = async () => {
    if (rating === 0) {
      toast({
        title: "Please Select a Rating",
        description: "Tap the stars to rate your driver",
        variant: "destructive"
      });
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const params = new URLSearchParams();
      params.append('rating', rating);
      if (comment.trim()) {
        params.append('comment', comment.trim());
      }
      
      await axios.post(`${BACKEND_URL}/api/users/rides/${rideId}/rate?${params}`);
      
      setIsSubmitted(true);
      toast({
        title: "Thank You!",
        description: "Your rating helps improve our service",
      });
      
      if (onRatingSubmitted) {
        onRatingSubmitted(rating);
      }
      
      // Close after delay
      setTimeout(() => {
        onClose();
      }, 2000);
      
    } catch (error) {
      console.error('Error submitting rating:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to submit rating",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const ratingLabels = ['', 'Poor', 'Fair', 'Good', 'Great', 'Excellent'];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-md p-6 relative animate-in fade-in zoom-in duration-200">
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
          disabled={isSubmitting}
        >
          <X size={20} />
        </button>
        
        {isSubmitted ? (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="text-green-600" size={32} />
            </div>
            <h3 className="text-xl font-bold mb-2">Thank You!</h3>
            <p className="text-gray-600">Your feedback helps us improve</p>
            <div className="flex justify-center gap-1 mt-4">
              {[1, 2, 3, 4, 5].map((star) => (
                <Star 
                  key={star}
                  size={24}
                  className={star <= rating ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}
                />
              ))}
            </div>
          </div>
        ) : (
          <>
            <div className="text-center mb-6">
              <h3 className="text-xl font-bold mb-1">Rate Your Ride</h3>
              <p className="text-gray-600">How was your trip with {driverName || 'your driver'}?</p>
            </div>
            
            {/* Star Rating */}
            <div className="flex justify-center gap-2 mb-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setHoveredRating(star)}
                  onMouseLeave={() => setHoveredRating(0)}
                  className="p-1 transition-transform hover:scale-110 focus:outline-none"
                  disabled={isSubmitting}
                  data-testid={`star-${star}`}
                >
                  <Star 
                    size={40}
                    className={`transition-colors ${
                      star <= (hoveredRating || rating) 
                        ? 'fill-amber-400 text-amber-400' 
                        : 'text-gray-300 hover:text-amber-200'
                    }`}
                  />
                </button>
              ))}
            </div>
            
            {/* Rating Label */}
            <p className="text-center text-sm font-medium text-amber-600 mb-6 h-5">
              {ratingLabels[hoveredRating || rating] || 'Tap to rate'}
            </p>
            
            {/* Comment */}
            <div className="mb-6">
              <Textarea
                placeholder="Add a comment (optional)"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                className="resize-none border-2"
                rows={3}
                disabled={isSubmitting}
              />
            </div>
            
            {/* Quick Feedback Chips */}
            <div className="flex flex-wrap gap-2 mb-6 justify-center">
              {['Great driver', 'Clean car', 'Safe driving', 'Friendly', 'On time'].map((chip) => (
                <button
                  key={chip}
                  onClick={() => setComment(prev => prev ? `${prev}, ${chip}` : chip)}
                  className="px-3 py-1 text-sm bg-gray-100 hover:bg-amber-100 rounded-full transition-colors"
                  disabled={isSubmitting}
                >
                  {chip}
                </button>
              ))}
            </div>
            
            {/* Submit Button */}
            <Button 
              onClick={handleSubmit}
              className="w-full bg-gradient-to-r from-amber-500 to-yellow-600 hover:from-amber-600 hover:to-yellow-700 py-6"
              disabled={isSubmitting || rating === 0}
              data-testid="submit-rating-btn"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 animate-spin" size={18} />
                  Submitting...
                </>
              ) : (
                'Submit Rating'
              )}
            </Button>
            
            <button
              onClick={onClose}
              className="w-full text-center text-gray-500 text-sm mt-3 hover:text-gray-700"
              disabled={isSubmitting}
            >
              Maybe later
            </button>
          </>
        )}
      </Card>
    </div>
  );
};

export default RatingModal;
